// =====================================================
// SPECIALTIES API CLIENT
// =====================================================

import { api } from './client';
import type {
  Specialty,
  SpecialtySelect,
  CreateSpecialtyRequest,
  UpdateSpecialtyRequest,
} from '../types/catalog-admin';
import type { PaginationQuery, PaginatedResponse } from '../types/pagination';

const BASE_URL = '/specialties';

export const specialtiesApi = {
  /**
   * Listar todas las especialidades (sin paginación - mantener compatibilidad)
   */
  getAll: async (includeInactive = false): Promise<Specialty[]> => {
    const query = includeInactive ? '?includeInactive=true' : '';
    return api.get<Specialty[]>(`${BASE_URL}${query}`);
  },

  /**
   * Listar especialidades con paginación
   */
  getAllPaginated: async (params: PaginationQuery = {}): Promise<PaginatedResponse<Specialty>> => {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.search) searchParams.set('search', params.search);
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params.includeInactive) searchParams.set('includeInactive', 'true');

    const queryString = searchParams.toString();
    const url = `${BASE_URL}/paginated${queryString ? `?${queryString}` : ''}`;
    
    return api.get<PaginatedResponse<Specialty>>(url);
  },

  /**
   * Obtener lista para select (solo activas)
   */
  getForSelect: async (): Promise<SpecialtySelect[]> => {
    return api.get<SpecialtySelect[]>(`${BASE_URL}/select`);
  },

  /**
   * Obtener una especialidad por ID
   */
  getById: async (id: string): Promise<Specialty> => {
    return api.get<Specialty>(`${BASE_URL}/${id}`);
  },

  /**
   * Crear nueva especialidad
   */
  create: async (data: CreateSpecialtyRequest): Promise<Specialty> => {
    return api.post<Specialty>(BASE_URL, data);
  },

  /**
   * Actualizar especialidad
   */
  update: async (id: string, data: UpdateSpecialtyRequest): Promise<Specialty> => {
    return api.patch<Specialty>(`${BASE_URL}/${id}`, data);
  },

  /**
   * Desactivar especialidad (soft delete)
   */
  deactivate: async (id: string): Promise<Specialty> => {
    return api.delete<Specialty>(`${BASE_URL}/${id}`);
  },

  /**
   * Reactivar especialidad
   */
  reactivate: async (id: string): Promise<Specialty> => {
    return api.patch<Specialty>(`${BASE_URL}/${id}/reactivate`);
  },
};

export default specialtiesApi;
